import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useJourney } from "@/hooks/useJourney";
import JourneyWizard from "@/features/journey/JourneyWizard";

const PHASE_COLORS = {
  descobrir: "var(--teal)",
  combinar: "var(--coffee)",
  planejar: "var(--coral)",
  executar: "var(--coffee-medium)",
  refletir: "var(--teal)",
};

export default function NextStepCard() {
  const { data, isLoading } = useJourney();
  const [wizardOpen, setWizardOpen] = useState(false);
  if (isLoading || !data || !data.next) return null;

  const { next, setup_done, setup_total, headline, coach_open, mood_kicker, progress } = data;
  const color = PHASE_COLORS[next.phase] || "var(--coffee)";

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="mt-8 relative paper-dark p-5 overflow-hidden"
      data-testid="next-step-card"
    >
      <span className="washi coral" />

      <div className="flex items-start gap-2 mb-3">
        <Compass size={16} className="text-[var(--coral)] mt-0.5 shrink-0" />
        <p className="eyebrow text-[var(--cream)]/80">{mood_kicker || `próximo passo · fase ${next.phase}`}</p>
      </div>

      <h2 className="serif text-2xl leading-[1.1] text-[var(--cream)]">{next.title}</h2>
      <p className="text-sm text-[var(--cream)]/70 mt-2">{next.subtitle}</p>
      <p className="hand text-lg text-[var(--coral)] mt-3">{headline}</p>

      {coach_open && (
        <p className="text-xs text-[var(--cream)]/60 mt-2 italic leading-relaxed">“{coach_open}”</p>
      )}

      <p className="text-xs text-[var(--cream)]/60 mt-3 italic">Por quê: {next.why}</p>

      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={() => setWizardOpen(true)}
          className="inline-flex items-center gap-2 bg-[var(--cream)] text-[var(--coffee)] font-semibold px-5 h-11 rounded-full transition-transform active:scale-[0.97]"
          style={{ boxShadow: `0 6px 18px -8px ${color}` }}
          data-testid="next-step-cta"
        >
          {next.cta} <ArrowRight size={16} />
        </button>
        <Link
          to="/jornada"
          className="text-xs text-[var(--cream)]/70 underline underline-offset-4 min-h-[44px] flex items-center"
          data-testid="next-step-open-journey"
        >
          ver a jornada
        </Link>
      </div>

      {setup_total > 0 && (
        <div className="mt-5 pt-4 border-t border-[var(--cream)]/15">
          <div className="flex items-center justify-between text-xs text-[var(--cream)]/70">
            <span>base do casal</span>
            <span className="num font-semibold text-[var(--cream)]">{setup_done}/{setup_total}</span>
          </div>
          <div className="mt-2 h-1 bg-[var(--cream)]/15 rounded-full overflow-hidden">
            <motion.div
              className="h-full"
              style={{ background: "var(--coral)" }}
              initial={{ width: 0 }}
              animate={{ width: `${Math.round(progress * 100)}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
        </div>
      )}

      <JourneyWizard stepId={next.id} open={wizardOpen} onOpenChange={setWizardOpen} />
    </motion.section>
  );
}

export function CompletedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--teal)]">
      <Check size={14} /> feito
    </span>
  );
}
